import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { Invoice, InvoiceChangeActor, InvoiceItem } from '../../types/unified-financial';
import {
  DeleteInvoiceItemInput,
  InvoiceChangeLogEntry,
  InvoiceService,
  ReclassifyInvoiceItemInput,
  ReplaceInvoiceItemsInput,
} from '../../services/InvoiceService';
import { toast } from 'react-toastify';

type InvoiceWithPatient = Invoice & { patientName: string; patientId: string };

interface EditableInvoiceItem extends InvoiceItem {
  invoiceItemId: string;
}

interface InvoiceEditModalProps {
  isOpen: boolean;
  invoice: InvoiceWithPatient | null;
  actor: InvoiceChangeActor | null;
  onClose: () => void;
  onUpdated: () => Promise<void>;
}

const normalizeDateForInput = (dateValue: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function InvoiceEditModal({ isOpen, invoice, actor, onClose, onUpdated }: InvoiceEditModalProps) {
  const [items, setItems] = useState<EditableInvoiceItem[]>([]);
  const [logs, setLogs] = useState<InvoiceChangeLogEntry[]>([]);
  const [reclassifyValidationLog, setReclassifyValidationLog] = useState<string | null>(null);
  const [isLogsExpanded, setIsLogsExpanded] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isCheckingClose, setIsCheckingClose] = useState(false);
  const [showDiscardChangesConfirm, setShowDiscardChangesConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentTotal, setCurrentTotal] = useState(0);
  const isEditableStatus = invoice?.status === 'open' || invoice?.status === 'pending';
  const isAdmin = actor?.userRole === 'admin';
  const canEdit = Boolean(isEditableStatus && isAdmin);

  useEffect(() => {
    if (!invoice) {
      setItems([]);
      return;
    }

    const mapped = (invoice.items ?? [])
      .filter((item): item is EditableInvoiceItem => Boolean(item.invoiceItemId))
      .map((item) => ({
        ...item,
        invoiceItemId: item.invoiceItemId!,
        description: item.description ?? '',
        amount: Number(item.amount || 0),
        date: normalizeDateForInput(item.date),
      }));

    setItems(mapped);
    setCurrentTotal(Number(invoice.total || 0));
    setReclassifyValidationLog(null);
  }, [invoice]);

  const getLogLabel = (changeType: InvoiceChangeLogEntry['invoiceLogChangeType']) => {
    if (changeType === 'replace_items') return 'Itens atualizados';
    if (changeType === 'update_item') return 'Itens atualizados';
    if (changeType === 'delete_item') return 'Itens deletados';
    if (changeType === 'reclassify') return 'Item reclassificado';
    if (changeType === 'status_change') return 'Status da fatura alterado';
    return changeType;
  };

  const loadLogs = async () => {
    if (!invoice || !actor) {
      setLogs([]);
      return;
    }

    try {
      setIsLoadingLogs(true);
      const logRows = await InvoiceService.getInvoiceChangeLogs({
        invoiceId: invoice.invoiceId,
        patientId: invoice.patientId,
        actor,
      });
      setLogs(logRows);
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao carregar histórico da fatura.');
      setLogs([]);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setIsLogsExpanded(false);
  }, [isOpen, invoice?.invoiceId]);

  useEffect(() => {
    if (!isOpen || !isLogsExpanded) return;
    loadLogs();
  }, [isOpen, isLogsExpanded, invoice?.invoiceId, invoice?.patientId, actor?.clientId]);

  const projectedTotal = useMemo(() => {
    return items.reduce((acc, item) => acc + Number(item.amount || 0), 0);
  }, [items]);

  if (!isOpen || !invoice) {
    return null;
  }

  const withReason = (title: string): string | null => {
    const reason = window.prompt(title);
    if (!reason || !reason.trim()) {
      toast.warning('Justificativa é obrigatória para esta operação.');
      return null;
    }
    return reason.trim();
  };

  const updateLocalItem = (invoiceItemId: string, patch: Partial<EditableInvoiceItem>) => {
    setItems((prev) => prev.map((item) => (item.invoiceItemId === invoiceItemId ? { ...item, ...patch } : item)));
  };

  const runMutation = async <T,>(
    fn: () => Promise<T>,
    options?: { closeAfter?: boolean; refreshAfter?: boolean; onError?: (message: string) => void }
  ): Promise<T | undefined> => {
    const closeAfter = options?.closeAfter ?? false;
    const refreshAfter = options?.refreshAfter ?? false;

    try {
      setIsSaving(true);
      const result = await fn();
      if (refreshAfter) {
        await onUpdated();
      }
      if (closeAfter) {
        onClose();
      }
      return result;
    } catch (error: any) {
      const message = error?.message || 'Erro ao atualizar fatura.';
      options?.onError?.(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const hasDifferentAmountFromDatabase = async (): Promise<boolean> => {
    if (!invoice || !actor) {
      return false;
    }

    const currentValues = await InvoiceService.getInvoiceItemsCurrentValues({
      invoiceId: invoice.invoiceId,
      patientId: invoice.patientId,
      actor,
    });

    const dbMap = new Map<string, number>(
      currentValues.map((row) => [row.invoiceItemId, Number(row.amount || 0)])
    );

    return items.some((item) => {
      if (!dbMap.has(item.invoiceItemId)) {
        return false;
      }
      const dbValue = Number(dbMap.get(item.invoiceItemId) || 0);
      const localValue = Number(item.amount || 0);
      return Math.abs(localValue - dbValue) >= 0.01;
    });
  };

  const refreshAndClose = async () => {
    try {
      await onUpdated();
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao atualizar dados da fatura antes de fechar.');
    } finally {
      onClose();
    }
  };

  const handleRequestClose = async () => {
    if (isSaving || isCheckingClose) {
      return;
    }

    if (!canEdit) {
      await refreshAndClose();
      return;
    }

    try {
      setIsCheckingClose(true);
      const hasPendingAmountChanges = await hasDifferentAmountFromDatabase();
      if (hasPendingAmountChanges) {
        setShowDiscardChangesConfirm(true);
        return;
      }
      await refreshAndClose();
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao validar alterações antes de fechar.');
    } finally {
      setIsCheckingClose(false);
    }
  };

  const handleDeleteItem = async (item: EditableInvoiceItem) => {
    if (!actor || !canEdit) return;
    const confirmDelete = window.confirm('Confirma a exclusão deste item da fatura?');
    if (!confirmDelete) return;

    try {
      setIsSaving(true);
      await InvoiceService.validateDeleteInvoiceItemEligibility({
        invoiceId: invoice.invoiceId,
        invoiceItemId: item.invoiceItemId,
        patientId: invoice.patientId,
        actor,
      });
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível validar a exclusão do item.');
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }

    const reason = withReason('Informe a justificativa para excluir este item:');
    if (!reason) return;

    const payload: DeleteInvoiceItemInput = {
      invoiceId: invoice.invoiceId,
      invoiceItemId: item.invoiceItemId,
      patientId: invoice.patientId,
      actor,
      reason,
    };

    const result = await runMutation(async () => {
      return InvoiceService.deleteInvoiceItem(payload);
    });

    if (result) {
      setItems((prev) => prev.filter((current) => current.invoiceItemId !== item.invoiceItemId));
      setCurrentTotal(result.total);
      await loadLogs();
      toast.success('Item removido com sucesso.');
    }
  };

  const handleReclassify = async (item: EditableInvoiceItem) => {
    if (!actor || !canEdit) return;
    setReclassifyValidationLog(null);

    try {
      setIsSaving(true);
      await InvoiceService.validateReclassifyInvoiceItemEligibility({
        invoiceId: invoice.invoiceId,
        invoiceItemId: item.invoiceItemId,
        patientId: invoice.patientId,
        actor,
        targetType: 'package',
      });
    } catch (error: any) {
      const message = error?.message || 'Não foi possível validar a reclassificação do item.';
      setReclassifyValidationLog(message);
      toast.error(message);
      return;
    } finally {
      setIsSaving(false);
    }

    const reason = withReason('Informe a justificativa para reclassificar este item:');
    if (!reason) return;

    const payload: ReclassifyInvoiceItemInput = {
      invoiceId: invoice.invoiceId,
      invoiceItemId: item.invoiceItemId,
      patientId: invoice.patientId,
      actor,
      targetType: 'package',
      reason,
    };

    const result = await runMutation(async () => {
      return InvoiceService.reclassifyInvoiceItem(payload);
    }, {
      onError: (message) => setReclassifyValidationLog(message),
    });

    if (result) {
      setItems((prev) =>
        prev.map((current) =>
          current.invoiceItemId === item.invoiceItemId
            ? { ...current, type: 'package', amount: 0 }
            : current
        )
      );
      setCurrentTotal(result.total);
      setReclassifyValidationLog(null);
      await loadLogs();
      toast.success('Item reclassificado com sucesso.');
    }
  };

  const handleSaveAll = async () => {
    if (!actor || !canEdit) return;
    const reason = withReason('Informe a justificativa para salvar as alterações da fatura:');
    if (!reason) return;

    const payload: ReplaceInvoiceItemsInput = {
      invoiceId: invoice.invoiceId,
      patientId: invoice.patientId,
      actor,
      reason,
      items: items.map((item) => ({
        description: item.description,
        amount: Number(item.amount),
        date: item.date,
        type: item.type,
      })),
    };

    const result = await runMutation(
      async () => {
        return InvoiceService.replaceInvoiceItems(payload);
      },
      { refreshAfter: true, closeAfter: true }
    );

    if (result) {
      setCurrentTotal(result.total);
      toast.success('Alterações salvas com sucesso.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto relative">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Editar Fatura</h3>
            <p className="text-sm text-gray-600">
              {invoice.patientName} • #{invoice.invoiceId}
            </p>
          </div>
          <button
            onClick={handleRequestClose}
            className="p-2 rounded-lg hover:bg-gray-100"
            disabled={isSaving || isCheckingClose}
          >
            ×
          </button>
        </div>

        {!canEdit && (
          <div className="m-5 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div className="text-sm">
              {!isEditableStatus
                ? 'Esta fatura não pode ser alterada porque não está com status open/pending.'
                : 'Somente perfis de administrador podem alterar itens da fatura.'}
            </div>
          </div>
        )}

        {reclassifyValidationLog && (
          <div className="m-5 p-4 rounded-lg border border-red-200 bg-red-50 text-red-900">
            <div className="text-sm font-semibold">Log da validação da reclassificação</div>
            <pre className="mt-2 text-xs whitespace-pre-wrap break-words">
              {reclassifyValidationLog}
            </pre>
          </div>
        )}

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-gray-50 border rounded-lg p-3">
              <div className="text-xs text-gray-500">Status</div>
              <div className="font-semibold text-gray-900">{invoice.status}</div>
            </div>
            <div className="bg-gray-50 border rounded-lg p-3">
              <div className="text-xs text-gray-500">Total Atual</div>
              <div className="font-semibold text-gray-900">R$ {currentTotal.toFixed(2)}</div>
            </div>
            <div className="bg-gray-50 border rounded-lg p-3">
              <div className="text-xs text-gray-500">Total Projetado</div>
              <div className={`font-semibold ${projectedTotal < 0 ? 'text-red-600' : 'text-blue-700'}`}>
                R$ {projectedTotal.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Descrição</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Data</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Valor</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item) => (
                  <tr key={item.invoiceItemId}>
                    <td className="px-3 py-2 align-top min-w-[220px]">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateLocalItem(item.invoiceItemId, { description: e.target.value })}
                        disabled={!canEdit || isSaving}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2 align-top min-w-[140px]">
                      <input
                        type="date"
                        value={item.date}
                        onChange={(e) => updateLocalItem(item.invoiceItemId, { date: e.target.value })}
                        disabled={!canEdit || isSaving}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2 align-top min-w-[130px]">
                      <span className="inline-flex px-2 py-1 rounded bg-gray-100 text-gray-800 text-xs font-medium">
                        {item.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top min-w-[130px]">
                      <input
                        type="number"
                        value={item.amount}
                        step="0.01"
                        min="0"
                        onChange={(e) => updateLocalItem(item.invoiceItemId, { amount: Number(e.target.value) })}
                        disabled={!canEdit || isSaving}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2 align-top min-w-[320px]">
                      <div className="flex flex-wrap gap-2">
                        {item.type === 'session' && (
                          <button
                            onClick={() => handleReclassify(item)}
                            disabled={!canEdit || isSaving}
                            className="px-2 py-1 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50"
                          >
                            Reclassificar para pacote
                          </button>
                        )}

                        <button
                          onClick={() => handleDeleteItem(item)}
                          disabled={!canEdit || isSaving}
                          className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-500">
                      Nenhum item com `invoiceItemId` disponível para edição.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t pt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <button
              onClick={handleSaveAll}
              disabled={!canEdit || isSaving || items.length === 0}
              className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Salvar Alterações da Fatura
            </button>

            <div className="text-sm text-gray-600">
              Todas as alterações exigem justificativa e serão auditadas em `invoice_change_log`.
            </div>
          </div>

          <div className="border rounded-lg">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3">
              <h4 className="text-sm font-semibold text-gray-800">Histórico de Alterações</h4>
              <button
                type="button"
                onClick={() => setIsLogsExpanded((prev) => !prev)}
                className="px-3 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800"
              >
                {isLogsExpanded ? 'Ocultar histórico' : 'Mostrar histórico'}
              </button>
            </div>
            {isLogsExpanded && (
              <div className="p-4 space-y-3">
              {isLoadingLogs && (
                <div className="text-sm text-gray-500">Carregando histórico...</div>
              )}

              {!isLoadingLogs && logs.length === 0 && (
                <div className="text-sm text-gray-500">Nenhuma alteração registrada para esta fatura.</div>
              )}

              {!isLoadingLogs && logs.map((log) => {
                const createdAt = new Date(log.invoiceLogCreatedAt);
                const createdAtLabel = Number.isNaN(createdAt.getTime())
                  ? log.invoiceLogCreatedAt
                  : createdAt.toLocaleString('pt-BR');
                const reason = log.invoiceLogDetails?.reason;

                return (
                  <div key={log.invoiceLogId} className="border rounded-lg p-3 bg-white">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="text-sm font-medium text-gray-900">
                        {getLogLabel(log.invoiceLogChangeType)}
                      </div>
                      <div className="text-xs text-gray-500">{createdAtLabel}</div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {log.invoiceLogActorDisplayName || 'Usuário não identificado'}
                      {log.invoiceLogActorEmail ? ` • ${log.invoiceLogActorEmail}` : ''}
                    </div>
                    {reason && (
                      <div className="text-sm text-gray-700 mt-2">
                        <span className="font-medium">Justificativa:</span> {String(reason)}
                      </div>
                    )}
                    <details className="mt-2">
                      <summary className="text-xs text-blue-700 cursor-pointer">Ver detalhes técnicos</summary>
                      <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto">
                        {JSON.stringify(log.invoiceLogDetails ?? {}, null, 2)}
                      </pre>
                    </details>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        </div>

        {showDiscardChangesConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-10">
            <div className="bg-white rounded-lg border shadow-lg max-w-md w-full p-4">
              <h5 className="text-base font-semibold text-gray-900">Descartar alterações?</h5>
              <p className="text-sm text-gray-600 mt-2">
                Existem alterações de valores não salvas. Deseja realmente fechar e descartar as alterações?
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowDiscardChangesConfirm(false)}
                  className="px-3 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Não
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setShowDiscardChangesConfirm(false);
                    await refreshAndClose();
                  }}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Sim
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
